// src/hooks/useQRCode.ts
import { useEffect, useRef, useCallback } from 'react';
import { QRCodeService } from '@/utils/qrcode/service';
import { QRStyleConfig, QRDownloadOptions } from '@/utils/qrcode/types';

export const useQRCode = (initialConfig: QRStyleConfig) => {
    const qrRef = useRef<QRCodeService | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const lastConfigRef = useRef<QRStyleConfig>(initialConfig);
    const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Initialize QR code only once
    const initQR = useCallback(() => {
        if (containerRef.current) {
            containerRef.current.innerHTML = '';
            qrRef.current = new QRCodeService(lastConfigRef.current);
            qrRef.current.appendToElement(containerRef.current);
        }
    }, []);

    // Force reinitialization only when value changes, not on every render
    useEffect(() => {
        // Only reinitialize if the value has actually changed
        if (initialConfig.value !== lastConfigRef.current.value) {
            console.log("QR code value changed, reinitializing");
            lastConfigRef.current = { ...lastConfigRef.current, value: initialConfig.value };
            initQR();
        }
        
        return () => {
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }
            if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
            }
        };
    }, [initialConfig.value, initQR]);

    // Handle image changes separately to avoid flickering
    useEffect(() => {
        if (initialConfig.image !== lastConfigRef.current.image) {
            console.log("QR code image changed:", initialConfig.image);
            lastConfigRef.current = { ...lastConfigRef.current, image: initialConfig.image };
            
            // Only update the image, don't reinitialize the whole QR code
            if (qrRef.current && containerRef.current) {
                qrRef.current.updateConfig({ image: initialConfig.image });
            } else {
                initQR();
            }
        }
    }, [initialConfig.image, initQR]);

    // Debounced update function to prevent rapid updates
    const updateQR = useCallback(async (config: Partial<QRStyleConfig>) => {
        // Update our reference to the latest config
        lastConfigRef.current = { ...lastConfigRef.current, ...config };
        
        // Clear any pending updates
        if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
        }
        
        // Debounce updates to prevent flickering
        updateTimeoutRef.current = setTimeout(async () => {
            if (!qrRef.current) {
                qrRef.current = new QRCodeService(lastConfigRef.current);
                if (containerRef.current) {
                    qrRef.current.appendToElement(containerRef.current);
                }
                return;
            }
            
            if (containerRef.current && qrRef.current) {
                // Make sure to pass the config to the QR code service
                await qrRef.current.updateConfig(config);
            }
        }, 100); // 100ms debounce
    }, []);

    const downloadQR = async (options: QRDownloadOptions) => {
        if (!qrRef.current) {
            qrRef.current = new QRCodeService(lastConfigRef.current);
        }
        await qrRef.current.downloadQR(options);
    };

    return {
        containerRef,
        updateQR,
        downloadQR
    };
};